import { useState } from "react";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";
import {
  errorMessage,
  requireMutationSuccess,
} from "../../utils/mutationResult.js";

import { Building2, FileText, Loader2 } from "lucide-react";
import { useNotification } from "../../hooks/useNotification";
import { useAppDispatch } from "../../redux/hooks";
import { createSiteData } from "../../redux/slices/siteDataSlice.js";
import { notificationHelpers } from "../../utils/notificationHelpers";
import { OptimizedInput, OptimizedSelect } from "../audit/optimized-form-field";

interface NewAuditModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSiteCreated: (siteId: string) => void;
}

const siteTypeOptions = [
  { value: "Commercial And Industrial", label: "Commercial and Industrial" },
  { value: "Utility", label: "Utility" },
  // { value: "Rooftop PV", label: "Rooftop PV" },
  // { value: "Ground Mount PV", label: "Ground Mount PV" },
  { value: "BESS", label: "BESS" },
  { value: "Co-located PV + BESS", label: "Co-located PV + BESS" },
];

const installationOptions = [
  { value: "Ground Mount", label: "Ground Mount" },
  { value: "Rooftop", label: "Rooftop" },
];
export function NewAuditModal({
  open,
  onOpenChange,
  onSiteCreated,
}: NewAuditModalProps) {
  const { notify } = useNotification();

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    projectId: null,
    siteName: "",
    location: "",
    siteType: "",
    installationType: "",
    template: "",
    description: "",
    contractor: "",
    projectManager: "",
  });

  const dispatch = useAppDispatch();
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const newSite = {
        siteName: formData.siteName,
        location: formData.location,
        siteType: formData.siteType,
        installationType: formData.installationType,
      };

      // Add site to the context
      const mutationResult1 = await requireMutationSuccess(
        dispatch(createSiteData(newSite)),
      );

      const config = notificationHelpers.formSaveSuccess("New Site Created", [
        mutationResult1,
      ]);
      notify(config.message, config.type, config.options);

      setLoading(false);
      onSiteCreated(
        mutationResult1.payload?._id || mutationResult1.payload?.id,
      );
    } catch (error) {
      const config = notificationHelpers.formSaveError(
        "Site Creation",
        errorMessage(error),
      );
      notify(config.message, config.type, config.options);
      setLoading(false);
      return;
    }

    // Reset form
    setFormData({
      projectId: null,
      siteName: "",
      location: "",
      siteType: "",
      installationType: "",
      template: "",
      description: "",
      contractor: "",
      projectManager: "",
    });
  };

  const isFormValid =
    formData.siteName &&
    formData.location &&
    formData.siteType &&
    formData.installationType;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[calc(100%-2rem)] max-w-[500px] max-h-[calc(100dvh-2rem)] overflow-y-auto overscroll-contain p-4 sm:max-w-[500px] sm:p-6">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 pr-6 text-left">
            <Building2 className="w-5 h-5 text-blue-600" />
            Create New Site
          </DialogTitle>
          <DialogDescription className="text-left">
            Set up a new site. Fill in the basic information to get started.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid min-w-0 grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="min-w-0 space-y-2">
              <OptimizedInput
                id="siteName"
                label="Site Name"
                value={formData.siteName}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, siteName: e }))
                }
                placeholder="e.g., Solar Farm Alpha"
                required
              />
            </div>
            <div className="min-w-0 space-y-2">
              <OptimizedInput
                id="location"
                label="Location"
                value={formData.location}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, location: e }))
                }
                placeholder="City, Country"
                required
              />
            </div>
          </div>
          <div className="grid min-w-0 grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="min-w-0 space-y-2">
              <OptimizedSelect
                id="siteType"
                label="Site Type"
                value={formData.siteType}
                onChange={(value) =>
                  setFormData((prev) => ({ ...prev, siteType: value }))
                }
                placeholder="Select site type"
                options={siteTypeOptions}
                required
                fullWidth
              />
            </div>
            <div className="min-w-0 space-y-2">
              <OptimizedSelect
                id="installationType"
                label="Installation Type"
                value={formData.installationType}
                onChange={(value) =>
                  setFormData((prev) => ({ ...prev, installationType: value }))
                }
                placeholder="Select installation type"
                options={installationOptions}
                required
                fullWidth
              />
            </div>
          </div>

          <DialogFooter className="gap-2 border-t border-slate-200 pt-4 dark:border-border">
            <Button
              type="button"
              variant="outline"
              className="w-full sm:w-auto"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="w-full sm:w-auto"
              disabled={!isFormValid || loading}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <FileText className="w-4 h-4 mr-2" />
                  Create Site
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
